// Store state management - simplified
const storeState = new Map(); // path -> current state
const storeSubscriptions = new Map(); // path -> Set of callbacks
const globalSubscriptions = new Set(); // Set of callbacks that fire on ANY store change
const storeHandlers = new Map(); // path -> { before: Map, on: Map, after: Map, error: Map, cleanup: Map }
const storeInitialState = new Map(); // path -> initial state
const actionToStoreMap = new Map(); // actionFn -> Set of store paths

const STORE_SYNC_CHANNEL = 'skybolt-store-sync';
const STORE_SYNC_EVENT_TYPE = 'store:update';
const STORE_SYNC_STORAGE_KEY = '__skybolt_store_sync__';

// RAF batching for store notifications
let rafScheduled = false;
const pendingNotifications = new Map(); // storePath -> { oldState, newState }

let storeSyncInitialized = false;
let storeSyncChannel = null;
let storeSyncStorageListener = null;
const storeSyncSourceId = createStoreSyncSourceId();
const storeSyncExcluded = new Set();

function createStoreSyncSourceId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `store-sync-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isBrowserEnvironment() {
  return typeof window !== 'undefined';
}

function ensureStoreSync() {
  if (storeSyncInitialized || !isBrowserEnvironment()) {
    return;
  }

  storeSyncInitialized = true;

  if (typeof globalThis.BroadcastChannel === 'function') {
    try {
      storeSyncChannel = new globalThis.BroadcastChannel(STORE_SYNC_CHANNEL);

      const onMessage = (event) => {
        handleIncomingStoreSync(event?.data);
      };

      if (typeof storeSyncChannel.addEventListener === 'function') {
        storeSyncChannel.addEventListener('message', onMessage);
      } else {
        storeSyncChannel.onmessage = onMessage;
      }

      return;
    } catch (error) {
      console.warn('Failed to initialize BroadcastChannel store sync:', error);
      storeSyncChannel = null;
    }
  }

  if (typeof window.addEventListener === 'function' && window.localStorage) {
    storeSyncStorageListener = (event) => {
      if (event.key !== STORE_SYNC_STORAGE_KEY || !event.newValue) {
        return;
      }

      try {
        handleIncomingStoreSync(JSON.parse(event.newValue));
      } catch (error) {
        console.warn('Failed to parse cross-tab store sync payload:', error);
      }
    };

    window.addEventListener('storage', storeSyncStorageListener);
  }
}

function cleanupStoreSync() {
  if (storeSyncChannel) {
    if (typeof storeSyncChannel.close === 'function') {
      storeSyncChannel.close();
    }
    storeSyncChannel = null;
  }

  if (storeSyncStorageListener && typeof window !== 'undefined') {
    window.removeEventListener('storage', storeSyncStorageListener);
    storeSyncStorageListener = null;
  }

  storeSyncInitialized = false;
}

function handleIncomingStoreSync(message) {
  if (!message || message.type !== STORE_SYNC_EVENT_TYPE) {
    return;
  }

  if (message.sourceId === storeSyncSourceId || typeof message.storePath !== 'string') {
    return;
  }

  if (storeSyncExcluded.has(message.storePath)) {
    return;
  }

  setStoreState(message.storePath, message.state, { source: 'remote' });
}

function broadcastStoreState(storePath, state) {
  if (storeSyncExcluded.has(storePath)) {
    return;
  }

  ensureStoreSync();

  const payload = {
    type: STORE_SYNC_EVENT_TYPE,
    sourceId: storeSyncSourceId,
    storePath,
    state,
    timestamp: Date.now()
  };

  if (storeSyncChannel) {
    try {
      storeSyncChannel.postMessage(payload);
    } catch (error) {
      console.warn('Failed to broadcast store update across tabs:', error);
    }
    return;
  }

  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(STORE_SYNC_STORAGE_KEY, JSON.stringify(payload));
    window.localStorage.removeItem(STORE_SYNC_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to persist cross-tab store sync payload:', error);
  }
}

/**
 * Registers a handler for a specific action at a specific store path
 * @param {string} storePath - The store path (derived from file path)
 * @param {string} phase - Lifecycle phase ('before', 'on', 'after', 'error', 'cleanup')
 * @param {Function|Function[]} actions - Action(s) to handle
 * @param {Function} handler - Handler function (state, context) => newState
 */
function registerHandler(storePath, phase, actions, handler) {
  ensureStoreSync();

  const actionList = Array.isArray(actions) ? actions : [actions];
  
  // Ensure handlers map exists for this store
  if (!storeHandlers.has(storePath)) {
    storeHandlers.set(storePath, {
      before: new Map(),
      on: new Map(),
      after: new Map(),
      error: new Map(),
      cleanup: new Map()
    });
  }
  
  const handlers = storeHandlers.get(storePath);
  
  actionList.forEach(action => {
    if (!action || typeof action !== 'function' || !action.name) {
      console.warn(`Invalid action passed to '${phase}' handler for store "${storePath}"`);
      return;
    }
    
    // Store the handler for this action
    handlers[phase].set(action, handler);
    
    // Update action-to-store mapping
    if (!actionToStoreMap.has(action)) {
      actionToStoreMap.set(action, new Set());
    }
    actionToStoreMap.get(action).add(storePath);
  });
}

/**
 * Creates handler registration functions for a specific store path
 * @param {string} storePath - The store path
 * @returns {Object} Handler registration functions
 */
export function createStoreHandlers(storePath) {
  return {
    before: (actions, handler) => registerHandler(storePath, 'before', actions, handler),
    on: (actions, handler) => registerHandler(storePath, 'on', actions, handler),
    after: (actions, handler) => registerHandler(storePath, 'after', actions, handler),
    error: (actions, handler) => registerHandler(storePath, 'error', actions, handler),
    cleanup: (actions, handler) => registerHandler(storePath, 'cleanup', actions, handler),
    // Set initial state for this store
    initialState: (state) => {
      ensureStoreSync();

      // Ensure the store is registered in the handlers map
      // This makes parsePath() and other functions recognize it as a valid store
      if (!storeHandlers.has(storePath)) {
        storeHandlers.set(storePath, {
          before: new Map(),
          on: new Map(),
          after: new Map(),
          error: new Map(),
          cleanup: new Map()
        });
      }
      
      storeInitialState.set(storePath, state);
      // Initialize the store state immediately if not already set
      if (!storeState.has(storePath)) {
        storeState.set(storePath, state);
      }
    }
  };
}

/**
 * Dispatches an action to all relevant stores
 * @param {Object} actionContext - The action context from global listener
 * @param {string} phase - The lifecycle phase
 * @param {...any} args - Additional arguments (result, error, etc.)
 */
export function dispatch(actionContext, phase, ...args) {
  // Find stores that respond to this action
  const matchingStorePaths = actionToStoreMap.get(actionContext.action);

  if (!matchingStorePaths || matchingStorePaths.size === 0) return;

  // Process each matching store
  matchingStorePaths.forEach(storePath => {
    const handlers = storeHandlers.get(storePath);
    if (!handlers) return;

    const handler = handlers[phase]?.get(actionContext.action);
    if (!handler || typeof handler !== 'function') return;

    try {
      const currentState = storeState.get(storePath) || {};
      
      // For error phase, attach the error to the context
      let contextWithPhaseData = actionContext;
      if (phase === 'error' && args.length > 0) {
        contextWithPhaseData = { ...actionContext, error: args[0] };
      }
      
      const newState = handler(currentState, contextWithPhaseData, ...args);
      
      // Early return if handler doesn't return new state
      if (newState === undefined) {
        return;
      }
      
      // Deep equality check - only update store if content has actually changed
      // This prevents infinite render loops when reducers return new objects with same content
      const isEqual = deepEqual(currentState, newState);
      
      if (!isEqual) {
        setStoreState(storePath, newState);
      }
    } catch (error) {
      console.warn(`Store reducer error in ${storePath} (${phase}):`, error);
    }
  });
}

/**
 * Selects data from store state by path
 * @param {string} path - The path to select (e.g., 'profiles/123', 'profiles')
 * @returns {any} The selected state, or initial state if not hydrated, or null if not found
 */
export function select(path) {
  const { storePath, key } = parsePath(path);
  let storeData = storeState.get(storePath);
  
  // If store doesn't have state yet but has initial state, use that
  if (storeData === undefined && storeInitialState.has(storePath)) {
    storeData = storeInitialState.get(storePath);
    // Initialize the store with its initial state
    storeState.set(storePath, storeData);
  }
  
  // Return null if store doesn't exist
  if (storeData === undefined) {
    return null;
  }
  
  if (key && storeData && typeof storeData === 'object') {
    // Try direct access first
    if (storeData[key] !== undefined) {
      return storeData[key];
    }

    // Try deep access for nested paths (e.g. "2/subtal123")
    if (key.includes('/')) {
      const parts = key.split('/');
      let current = storeData;
      for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
          current = current[part];
        } else {
          return null;
        }
      }
      return current;
    }

    return null;
  }
  
  return storeData;
}

/**
 * Alias for select (Redux-style naming)
 */
export const selectAll = select;

/**
 * Subscribes to state changes at a specific path
 * @param {string} path - The path to watch
 * @param {Function} callback - Called with (newState, oldState) when state changes
 * @returns {Function} Unsubscribe function
 */
export function subscribe(path, callback) {
  if (typeof callback !== 'function') {
    throw new Error('Subscribe callback must be a function');
  }

  const { storePath, key } = parsePath(path);
  
  if (key) {
    // Subscribe to specific key within store
    return subscribeToKey(storePath, key, callback);
  } else {
    // Subscribe to entire store
    return subscribeToStore(storePath, callback);
  }
}

/**
 * Alias for subscribe (Redux-style naming)
 */
export const subscribeAll = subscribe;

/**
 * Subscribes to ANY store change (fires whenever any store updates)
 * @param {Function} callback - Called with (allStores, changedStorePath) when any store changes
 * @returns {Function} Unsubscribe function
 */
export function subscribeToAllStores(callback) {
  if (typeof callback !== 'function') {
    throw new Error('subscribeToAllStores callback must be a function');
  }
  
  globalSubscriptions.add(callback);
  
  return () => {
    globalSubscriptions.delete(callback);
  };
}

/**
 * Gets current state for a store path, initializing if needed
 * @param {string} storePath - The store path
 * @param {Object} config - Store config for initialization
 * @returns {any} Current state
 */
function getStoreState(storePath, config) {
  if (!storeState.has(storePath)) {
    storeState.set(storePath, config.initialState ?? {});
  }
  return storeState.get(storePath);
}

/**
 * Sets new state for a store path and notifies subscribers
 * Optimized with:
 * - Strict equality check (prevents noop updates)
 * - RAF batching (batches multiple updates into single render)
 * 
 * @param {string} storePath - The store path
 * @param {any} newState - The new state
 */
function setStoreState(storePath, newState, options = {}) {
  const { source = 'local' } = options;
  const oldState = storeState.get(storePath);
  
  // Skip if no change (reference equality check)
  // Reducers MUST return new state objects for updates to be detected
  if (oldState === newState) {
    return;
  }
  
  storeState.set(storePath, newState);

  if (source === 'local') {
    broadcastStoreState(storePath, newState);
  }
  
  // Add to pending notifications
  // If we already have a pending notification for this path, we keep the ORIGINAL oldState
  // from the beginning of the frame, and update the newState to the latest.
  if (pendingNotifications.has(storePath)) {
    const pending = pendingNotifications.get(storePath);
    pending.newState = newState;
  } else {
    pendingNotifications.set(storePath, { oldState, newState });
  }
  
  // Schedule RAF flush if not already scheduled
  if (!rafScheduled) {
    rafScheduled = true;
    requestAnimationFrame(() => {
      rafScheduled = false;
      flushNotifications();
    });
  }
}

/**
 * Deep equality check for store state
 * @param {any} a 
 * @param {any} b 
 * @returns {boolean}
 */
function deepEqual(a, b) {
  if (a === b) return true;
  
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    if (a.constructor !== b.constructor) return false;
    
    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }
    
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    
    if (keysA.length !== keysB.length) return false;
    
    for (const key of keysA) {
      if (!b.hasOwnProperty(key)) return false;
      if (!deepEqual(a[key], b[key])) return false;
    }
    
    return true;
  }
  
  return false;
}

/**
 * Flushes pending store notifications (called via RAF)
 * Batches multiple store updates into a single notification cycle
 */
function flushNotifications() {
  if (pendingNotifications.size === 0) {
    return;
  }
  
  // Process all pending notifications
  const notifications = Array.from(pendingNotifications.entries());
  pendingNotifications.clear();
  
  const changedStorePaths = [];
  
  // Notify path-specific subscribers for each changed store
  notifications.forEach(([storePath, { oldState, newState }]) => {
    changedStorePaths.push(storePath);

    const subscribers = storeSubscriptions.get(storePath);
    if (subscribers) {
      subscribers.forEach(subscription => {
        try {
          if (subscription.key) {
            // Key-specific subscription
            const newValue = newState?.[subscription.key];
            const oldValue = oldState?.[subscription.key];
            
            if (newValue !== oldValue) {
              subscription.callback(newValue, oldValue);
            }
          } else {
            // Full store subscription
            subscription.callback(newState, oldState);
          }
        } catch (error) {
          console.warn('Store subscriber error:', error);
        }
      });
    }
  });
  
  // Notify global subscribers ONCE with all changes
  if (changedStorePaths.length > 0) {
    const allStores = getAllStoreStates();
    
    globalSubscriptions.forEach(callback => {
      try {
        callback(allStores, changedStorePaths);
      } catch (error) {
        console.warn('Global store subscriber error:', error);
      }
    });
  }
}

/**
 * Parses a path into store path and key components
 * @param {string} path - Path like 'profiles/123' or 'profiles'
 * @returns {Object} { storePath, key }
 */
function parsePath(path) {
  // Try to find matching store by testing progressively shorter paths
  const parts = path.split('/');
  
  for (let i = parts.length; i > 0; i--) {
    const testPath = parts.slice(0, i).join('/');
    if (storeHandlers.has(testPath)) {
      const key = parts.slice(i).join('/');
      return { 
        storePath: testPath, 
        key: key || null 
      };
    }
  }
  
  // Fallback: assume entire path is store path
  return { 
    storePath: path, 
    key: null 
  };
}

/**
 * Subscribes to a specific store (full state changes)
 * @param {string} storePath - Store path
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
function subscribeToStore(storePath, callback) {
  if (!storeSubscriptions.has(storePath)) {
    storeSubscriptions.set(storePath, new Set());
  }
  
  const subscription = { callback, key: null };
  storeSubscriptions.get(storePath).add(subscription);
  
  return () => {
    const subscribers = storeSubscriptions.get(storePath);
    if (subscribers) {
      subscribers.delete(subscription);
      if (subscribers.size === 0) {
        storeSubscriptions.delete(storePath);
      }
    }
  };
}

/**
 * Subscribes to a specific key within a store
 * @param {string} storePath - Store path
 * @param {string} key - Key within store
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
function subscribeToKey(storePath, key, callback) {
  if (!storeSubscriptions.has(storePath)) {
    storeSubscriptions.set(storePath, new Set());
  }
  
  const subscription = { callback, key };
  storeSubscriptions.get(storePath).add(subscription);
  
  return () => {
    const subscribers = storeSubscriptions.get(storePath);
    if (subscribers) {
      subscribers.delete(subscription);
      if (subscribers.size === 0) {
        storeSubscriptions.delete(storePath);
      }
    }
  };
}

/**
 * Gets all registered stores
 * @returns {Array} Array of store paths
 */
export function getAllStores() {
  return Array.from(storeHandlers.keys());
}

/**
 * Gets all current store states
 * @returns {Object} Map of store paths to their current state
 */
export function getAllStoreStates() {
  return Object.fromEntries(storeState.entries());
}

/**
 * Checks if a store is registered at the given path
 * @param {string} path - Store path
 * @returns {boolean} True if store exists
 */
export function hasStore(path) {
  return storeHandlers.has(path);
}

/**
 * Gets store registry statistics
 * @returns {Object} Registry stats
 */
export function getStoreStats() {
  return {
    stores: storeHandlers.size,
    subscriptions: storeSubscriptions.size,
    stateEntries: storeState.size
  };
}

/**
 * Clears all stores, state, and subscriptions (for testing)
 */
export function clearStores() {
  storeHandlers.clear();
  storeState.clear();
  storeInitialState.clear();
  storeSubscriptions.clear();
  globalSubscriptions.clear();
  actionToStoreMap.clear();
  storeSyncExcluded.clear();
  cleanupStoreSync();
}

/**
 * Hydrates stores with initial state data (for development/testing)
 * @param {Object} initialStates - Map of store paths to their initial state
 * @param {boolean} merge - If true, merges with existing state. If false, replaces state.
 */
/**
 * Excludes one or more store paths from cross-tab synchronization.
 * Updates to excluded stores will neither be broadcast to other tabs
 * nor applied when received from other tabs.
 * @param {string|string[]} paths - Store path(s) to exclude
 */
export function excludeFromSync(paths) {
  const list = Array.isArray(paths) ? paths : [paths];
  list.forEach(p => storeSyncExcluded.add(p));
}

export function hydrateStores(initialStates, merge = true) {
  if (!initialStates || typeof initialStates !== 'object') {
    console.warn('hydrateStores: initialStates must be an object');
    return;
  }

  Object.entries(initialStates).forEach(([storePath, state]) => {
    if (!storeHandlers.has(storePath)) {
      // Try to auto-create store if it doesn't exist
      // This is useful for dynamic paths like 'projects/1/talent'
      const handlers = createStoreHandlers(storePath);
      handlers.initialState(Array.isArray(state) ? [] : {});
    }

    const currentState = storeState.get(storePath) || {};
    const newState = merge ? { ...currentState, ...state } : state;
    
    setStoreState(storePath, newState);
  });
}
